import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import aliceGlitchImage from '../../assets/alice.a.jpg';

const MESSAGES = [
    "root@sys:~# grep -r 'SIGINT' /var/log/auth.log",
    "root@sys:~# netstat -an | grep 192.168.0.1",
    "root@sys:~# chmod 777 /dev/dsp",
    "root@sys:~# tar -xvf packet_capture_84.tar.gz",
    "root@sys:~# mount /dev/sda1 /mnt/data",
    "root@sys:~# ping -c 4 10.0.0.57",
    "root@sys:~# ps aux | grep 'daemon'",
    "root@sys:~# whoami",
    "root@sys:~# tail -f /var/log/syslog",
    "root@sys:~# /usr/bin/decrypt_stream --key=FA44",
    "Scanning port 80... OUVERT",
    "Scanning port 443... OUVERT",
    "Connexion réinitialisée par l'hôte",
    "Dépassement de tampon à 0x84F2A"
];

const getRandomMessage = () => {
    return MESSAGES[Math.floor(Math.random() * MESSAGES.length)];
};

export const TerminalLogs: React.FC = () => {
    const { t } = useLanguage();
    
    // Créer les logs système avec traductions
    const SYSTEM_LOGS = [
        {
            command: "RESEAU --STATUT",
            response: [
                `[SYS] [14-NOV-1996 21:04:12] [LOG] ${t('terminalLogs.systemLog.encryptionUpdate')}`,
                `[LOG] ${t('terminalLogs.systemLog.origin')}`,
                `> ${t('terminalLogs.systemLog.adminNote')}`,
                `${t('terminalLogs.systemLog.adminNote2')}`,
                `${t('terminalLogs.systemLog.adminNote3')}`,
                `${t('terminalLogs.systemLog.adminNote4')}`,
                `${t('terminalLogs.systemLog.adminNote5')}`,
                `${t('terminalLogs.systemLog.adminNote6')}`,
                `${t('terminalLogs.systemLog.adminNote7')}`
            ]
        }
    ];
    
    const [lines, setLines] = useState<string[]>([
        t('terminalLogs.bootSequence'),
        t('terminalLogs.loadingKernel'),
        t('terminalLogs.mountingVolumes'),
        t('terminalLogs.ready')
    ]);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [showInput, setShowInput] = useState(false);
    const [showGlitchImage, setShowGlitchImage] = useState(false);
    const [showRekaImage, setShowRekaImage] = useState(false);
    const [rekaImageFadeOut, setRekaImageFadeOut] = useState(false);
    const [rekaZoomLevel, setRekaZoomLevel] = useState(1);
    const [showRekaSecondImage, setShowRekaSecondImage] = useState(false);
    const [rekaSecondImageFadeOut, setRekaSecondImageFadeOut] = useState(false);
    const [hideLogsForReka, setHideLogsForReka] = useState(false);
    const [showRekaAlert, setShowRekaAlert] = useState(false);
    const [isGlitching, setIsGlitching] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const addLines = (newLines: string[]) => {
        setLines(prev => [...prev.slice(-35), ...newLines]); // Garder les 35 dernières lignes + nouvelles
    };

    const handleCommand = (command: string) => {
        const timestamp = new Date().toLocaleTimeString('fr-FR', { hour12: false });
        addLines([`[${timestamp}] > ${command}`]);
        
        const upperCommand = command.toUpperCase().trim();
        const systemLog = SYSTEM_LOGS.find(log => log.command === upperCommand);
        
        if (systemLog) {
            // Ajouter les lignes du log système avec un petit délai pour l'effet
            setTimeout(() => {
                addLines(systemLog.response);
            }, 200);
        } else if (upperCommand === 'HELP' || upperCommand === 'AIDE') {
            setTimeout(() => {
                addLines([
                    t('terminalLogs.availableCommands'),
                    `  ${t('terminalLogs.networkStatusCmd')}`,
                    `  ${t('terminalLogs.helpCmd')}`,
                    `  ${t('terminalLogs.clearCmd')}`
                ]);
            }, 200);
        } else if (upperCommand === 'CLEAR') {
            setLines([t('terminalLogs.terminalCleared')]);
        } else {
            setTimeout(() => {
                addLines([`${t('terminalLogs.unknownCommand')} : ${command}`, t('terminalLogs.typeHelpForHelp')]);
            }, 200);
        }
        
        setInputValue('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleCommand(inputValue);
        } else if (e.key === 'Escape') {
            setShowInput(false);
            setInputValue('');
        }
    };

    const handleScroll = () => {
        if (!scrollContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const atBottom = scrollHeight - scrollTop - clientHeight < 20; // 20px threshold
        setIsAtBottom(atBottom);
    };

    useEffect(() => {
        const handleScannerLog = (e: Event) => {
            const customEvent = e as CustomEvent;
            const msg = customEvent.detail.message;
            const timestamp = new Date().toLocaleTimeString('fr-FR', { hour12: false }) + '.' + Math.floor(Math.random() * 999).toString().padStart(3, '0');
            addLines([`[${timestamp}] ! ${msg}`]);
        };

        const handleGlitchImage = (e: Event) => {
            const customEvent = e as CustomEvent;
            setShowGlitchImage(customEvent.detail.show);
        };

        const handleRekaImage = (e: Event) => {
            const customEvent = e as CustomEvent;
            if (customEvent.detail.show) {
                setRekaImageFadeOut(false);
                setShowRekaImage(true);
                setRekaZoomLevel(1);
                setShowRekaSecondImage(false);
                setRekaSecondImageFadeOut(false);
                setHideLogsForReka(false);
                setShowRekaAlert(false);
                setIsGlitching(false);
                
                // Démarrer le suivi du zoom
                const startTime = Date.now();
                const zoomInterval = setInterval(() => {
                    const elapsed = Date.now() - startTime;
                    const totalDuration = 290000; // 290 secondes (4min 50s)
                    const zoomDuration = 270000; // Zoom jusqu'à 4min 30s
                    const progress = Math.min(elapsed / zoomDuration, 1);
                    const currentZoom = 1 + (5 * progress); // De 1 à 6
                    setRekaZoomLevel(currentZoom);
                    
                    // Phase 1: Fade-out de la photo famille (4min 30s → 4min 35s)
                    if (elapsed >= 270000 && elapsed < 275000 && !rekaImageFadeOut) {
                        setRekaImageFadeOut(true);
                        setTimeout(() => {
                            setShowRekaImage(false);
                            setRekaImageFadeOut(false);
                        }, 5000); // 5 secondes de fade-out
                    }
                    
                    // Phase 2: Apparition photo identité + masquer logs (4min 40s)
                    if (elapsed >= 280000 && !showRekaSecondImage) {
                        setShowRekaSecondImage(true);
                        setHideLogsForReka(true);
                    }
                    
                    // Phase 3: Alerte "ACTIF" (4min 45s)
                    if (elapsed >= 285000 && !showRekaAlert) {
                        setShowRekaAlert(true);
                    }
                    
                    // Phase 4: Fin naturelle avec glitch (4min 50s)
                    if (elapsed >= totalDuration && !isGlitching) {
                        setIsGlitching(true);
                        setShowRekaAlert(false);
                        
                        // Glitch pendant 2 secondes puis retour normal
                        setTimeout(() => {
                            setRekaSecondImageFadeOut(true);
                            setTimeout(() => {
                                setShowRekaSecondImage(false);
                                setRekaSecondImageFadeOut(false);
                                setHideLogsForReka(false);
                                setIsGlitching(false);
                            }, 1000);
                        }, 2000);
                        
                        clearInterval(zoomInterval);
                    }
                }, 100); // Mise à jour toutes les 100ms
                
                // Stocker l'interval pour le nettoyer plus tard
                (window as any).rekaZoomInterval = zoomInterval;
            } else {
                // Arrêter le suivi du zoom
                if ((window as any).rekaZoomInterval) {
                    clearInterval((window as any).rekaZoomInterval);
                }
                
                // Nettoyage immédiat de tous les états
                setRekaImageFadeOut(false);
                setShowRekaImage(false);
                setRekaZoomLevel(1);
                setRekaSecondImageFadeOut(false);
                setShowRekaSecondImage(false);
                setHideLogsForReka(false);
                setShowRekaAlert(false);
                setIsGlitching(false);
            }
        };

        window.addEventListener('SCANNER_LOG', handleScannerLog);
        window.addEventListener('GLITCH_IMAGE', handleGlitchImage);
        window.addEventListener('REKA_IMAGE', handleRekaImage);

        const interval = setInterval(() => {
            if (Math.random() > 0.3) return; // Randomiser le timing

            const msg = getRandomMessage();
            const timestamp = new Date().toLocaleTimeString('fr-FR', { hour12: false }) + '.' + Math.floor(Math.random() * 999).toString().padStart(3, '0');
            const newLine = `[${timestamp}] ${msg}`;

            addLines([newLine]);
        }, 1500);

        return () => {
            clearInterval(interval);
            window.removeEventListener('SCANNER_LOG', handleScannerLog);
            window.removeEventListener('GLITCH_IMAGE', handleGlitchImage);
            window.removeEventListener('REKA_IMAGE', handleRekaImage);
        };
    }, []);

    // Focus sur l'input quand il devient visible
    useEffect(() => {
        if (showInput && inputRef.current) {
            inputRef.current.focus();
        }
    }, [showInput]);

    useEffect(() => {
        if (isAtBottom) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [lines, isAtBottom]);

    return (
        <div className={`absolute inset-0 bg-black font-mono text-[12px] p-2 overflow-hidden flex flex-col ${isGlitching ? 'widget-glitching' : ''}`}>
            <style>{`
                @keyframes glitch-flicker {
                    0%, 100% { 
                        opacity: 0.08;
                        filter: brightness(0.3) contrast(2) hue-rotate(0deg);
                    }
                    5% { 
                        opacity: 0.25;
                        filter: brightness(1.5) contrast(3) hue-rotate(90deg);
                    }
                    10% { 
                        opacity: 0.12;
                        filter: brightness(0.5) contrast(1.5) hue-rotate(180deg);
                    }
                    15% { 
                        opacity: 0.3;
                        filter: brightness(2) contrast(4) hue-rotate(270deg);
                    }
                    20% { 
                        opacity: 0.1;
                        filter: brightness(0.4) contrast(2.5) hue-rotate(45deg);
                    }
                    25% { 
                        opacity: 0.28;
                        filter: brightness(1.8) contrast(3.5) hue-rotate(135deg);
                    }
                    30% { 
                        opacity: 0.15;
                        filter: brightness(0.6) contrast(2) hue-rotate(225deg);
                    }
                    35% { 
                        opacity: 0.22;
                        filter: brightness(1.2) contrast(3) hue-rotate(315deg);
                    }
                    40% { 
                        opacity: 0.18;
                        filter: brightness(0.7) contrast(2.2) hue-rotate(60deg);
                    }
                    45% { 
                        opacity: 0.26;
                        filter: brightness(1.6) contrast(3.2) hue-rotate(150deg);
                    }
                    50% { 
                        opacity: 0.14;
                        filter: brightness(0.5) contrast(2.8) hue-rotate(240deg);
                    }
                    55% { 
                        opacity: 0.32;
                        filter: brightness(1.9) contrast(3.8) hue-rotate(330deg);
                    }
                    60% { 
                        opacity: 0.2;
                        filter: brightness(0.8) contrast(2.3) hue-rotate(75deg);
                    }
                    65% { 
                        opacity: 0.24;
                        filter: brightness(1.3) contrast(3.1) hue-rotate(165deg);
                    }
                    70% { 
                        opacity: 0.16;
                        filter: brightness(0.6) contrast(2.6) hue-rotate(255deg);
                    }
                    75% { 
                        opacity: 0.29;
                        filter: brightness(1.7) contrast(3.6) hue-rotate(345deg);
                    }
                    80% { 
                        opacity: 0.19;
                        filter: brightness(0.7) contrast(2.4) hue-rotate(90deg);
                    }
                    85% { 
                        opacity: 0.27;
                        filter: brightness(1.4) contrast(3.3) hue-rotate(180deg);
                    }
                    90% { 
                        opacity: 0.13;
                        filter: brightness(0.5) contrast(2.7) hue-rotate(270deg);
                    }
                    95% { 
                        opacity: 0.23;
                        filter: brightness(1.5) contrast(3.4) hue-rotate(0deg);
                    }
                }
                
                @keyframes glitch-position {
                    0%, 100% { 
                        transform: translate(0, 0) scale(1);
                    }
                    10% { 
                        transform: translate(-8px, 5px) scale(1.03);
                    }
                    20% { 
                        transform: translate(10px, -8px) scale(0.98);
                    }
                    30% { 
                        transform: translate(-5px, -10px) scale(1.05);
                    }
                    40% { 
                        transform: translate(12px, 8px) scale(0.96);
                    }
                    50% { 
                        transform: translate(-10px, 3px) scale(1.04);
                    }
                    60% { 
                        transform: translate(8px, -12px) scale(0.97);
                    }
                    70% { 
                        transform: translate(-3px, 10px) scale(1.02);
                    }
                    80% { 
                        transform: translate(5px, -5px) scale(0.99);
                    }
                    90% { 
                        transform: translate(-8px, 8px) scale(1.03);
                    }
                }
                
                .glitch-image-container {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    pointer-events: none;
                    z-index: 0;
                    overflow: hidden;
                }
                
                .glitch-image {
                    animation: glitch-flicker 0.15s infinite, glitch-position 0.3s infinite;
                    mix-blend-mode: screen;
                    pointer-events: none;
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }
                
                @keyframes reka-fade-in-only {
                    0% { 
                        opacity: 0;
                    }
                    100% { 
                        opacity: 0.55;
                    }
                }
                
                @keyframes reka-zoom-progressive {
                    0% { 
                        transform: scale(1) translate(0%, 0%);
                    }
                    100% { 
                        transform: scale(6) translate(15%, 8%);
                    }
                }
                
                @keyframes reka-fade-out {
                    0% { 
                        opacity: 0.25;
                    }
                    100% { 
                        opacity: 0;
                    }
                }
                
                @keyframes reka-instability {
                    0%, 100% { 
                        opacity: 0.45;
                        filter: sepia(0.3) contrast(1.2) brightness(0.8) blur(0px);
                    }
                    5% { 
                        opacity: 0.35;
                        filter: sepia(0.4) contrast(1.4) brightness(0.7) blur(0.5px);
                    }
                    10% { 
                        opacity: 0.55;
                        filter: sepia(0.2) contrast(1.0) brightness(0.9) blur(0px);
                    }
                    15% { 
                        opacity: 0.30;
                        filter: sepia(0.5) contrast(1.6) brightness(0.6) blur(1px);
                    }
                    20% { 
                        opacity: 0.50;
                        filter: sepia(0.3) contrast(1.3) brightness(0.8) blur(0px);
                    }
                    25% { 
                        opacity: 0.25;
                        filter: sepia(0.6) contrast(1.8) brightness(0.5) blur(1.5px);
                    }
                    30% { 
                        opacity: 0.52;
                        filter: sepia(0.2) contrast(1.1) brightness(0.85) blur(0px);
                    }
                    35% { 
                        opacity: 0.38;
                        filter: sepia(0.4) contrast(1.5) brightness(0.65) blur(0.5px);
                    }
                    40% { 
                        opacity: 0.48;
                        filter: sepia(0.3) contrast(1.2) brightness(0.75) blur(0px);
                    }
                    45% { 
                        opacity: 0.28;
                        filter: sepia(0.5) contrast(1.7) brightness(0.55) blur(1px);
                    }
                    50% { 
                        opacity: 0.46;
                        filter: sepia(0.25) contrast(1.15) brightness(0.82) blur(0px);
                    }
                    55% { 
                        opacity: 0.32;
                        filter: sepia(0.45) contrast(1.45) brightness(0.62) blur(0.5px);
                    }
                    60% { 
                        opacity: 0.54;
                        filter: sepia(0.2) contrast(1.05) brightness(0.88) blur(0px);
                    }
                    65% { 
                        opacity: 0.36;
                        filter: sepia(0.4) contrast(1.35) brightness(0.68) blur(0.5px);
                    }
                    70% { 
                        opacity: 0.49;
                        filter: sepia(0.3) contrast(1.25) brightness(0.78) blur(0px);
                    }
                    75% { 
                        opacity: 0.27;
                        filter: sepia(0.55) contrast(1.65) brightness(0.52) blur(1px);
                    }
                    80% { 
                        opacity: 0.47;
                        filter: sepia(0.25) contrast(1.18) brightness(0.8) blur(0px);
                    }
                    85% { 
                        opacity: 0.34;
                        filter: sepia(0.42) contrast(1.42) brightness(0.64) blur(0.5px);
                    }
                    90% { 
                        opacity: 0.51;
                        filter: sepia(0.22) contrast(1.08) brightness(0.86) blur(0px);
                    }
                    95% { 
                        opacity: 0.39;
                        filter: sepia(0.38) contrast(1.32) brightness(0.72) blur(0.5px);
                    }
                }
                
                @keyframes reka-micro-movements-no-transform {
                    0%, 100% { 
                        filter: sepia(0.3) contrast(1.2) brightness(0.8) blur(0px);
                    }
                    25% { 
                        filter: sepia(0.5) contrast(1.5) brightness(0.6) blur(1px);
                    }
                    50% { 
                        filter: sepia(0.2) contrast(1.0) brightness(1.0) blur(0px);
                    }
                    75% { 
                        filter: sepia(0.4) contrast(1.3) brightness(0.7) blur(0.5px);
                    }
                }
                
                .reka-image-container {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    pointer-events: none;
                    z-index: 0;
                    overflow: hidden;
                    background: linear-gradient(90deg, 
                        rgba(0,0,0,0.9) 0%, 
                        rgba(0,0,0,0.7) 10%, 
                        rgba(0,0,0,0.3) 20%, 
                        transparent 30%, 
                        transparent 70%, 
                        rgba(0,0,0,0.3) 80%, 
                        rgba(0,0,0,0.7) 90%, 
                        rgba(0,0,0,0.9) 100%
                    );
                }
                
                .reka-image-wrapper {
                    position: relative;
                    width: 60%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }
                
                .reka-second-image-container {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    pointer-events: none;
                    z-index: 1;
                    overflow: hidden;
                    background: linear-gradient(90deg, 
                        rgba(0,0,0,0.8) 0%, 
                        rgba(0,0,0,0.3) 15%, 
                        rgba(0,0,0,0.1) 25%, 
                        transparent 35%, 
                        transparent 65%, 
                        rgba(0,0,0,0.1) 75%, 
                        rgba(0,0,0,0.3) 85%, 
                        rgba(0,0,0,0.8) 100%
                    );
                }
                
                .reka-second-image-wrapper {
                    position: relative;
                    width: 70%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }
                
                .reka-second-image {
                    mix-blend-mode: multiply;
                    pointer-events: none;
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                    opacity: 0.4;
                    filter: sepia(0.3) contrast(1.2) brightness(0.8);
                }
                
                @keyframes reka-alert-blink {
                    0% { 
                        opacity: 1;
                        box-shadow: 
                            0 0 30px rgba(255, 0, 0, 1),
                            0 0 60px rgba(255, 0, 0, 0.8),
                            inset 0 0 20px rgba(255, 100, 100, 0.3);
                    }
                    50% { 
                        opacity: 0.4;
                        box-shadow: 
                            0 0 15px rgba(255, 0, 0, 0.6),
                            0 0 30px rgba(255, 0, 0, 0.4),
                            inset 0 0 10px rgba(255, 100, 100, 0.1);
                    }
                    100% { 
                        opacity: 1;
                        box-shadow: 
                            0 0 30px rgba(255, 0, 0, 1),
                            0 0 60px rgba(255, 0, 0, 0.8),
                            inset 0 0 20px rgba(255, 100, 100, 0.3);
                    }
                }

                @keyframes reka-alert-pulse {
                    0% { 
                        background: linear-gradient(45deg, #ff0000, #cc0000, #ff0000, #cc0000);
                        border-color: #ff0000;
                    }
                    25% { 
                        background: linear-gradient(45deg, #ff3333, #ff0000, #ff3333, #ff0000);
                        border-color: #ff3333;
                    }
                    50% { 
                        background: linear-gradient(45deg, #ff0000, #990000, #ff0000, #990000);
                        border-color: #ff0000;
                    }
                    75% { 
                        background: linear-gradient(45deg, #ff6666, #ff0000, #ff6666, #ff0000);
                        border-color: #ff6666;
                    }
                    100% { 
                        background: linear-gradient(45deg, #ff0000, #cc0000, #ff0000, #cc0000);
                        border-color: #ff0000;
                    }
                }

                @keyframes reka-alert-text-glow {
                    0% { 
                        text-shadow: 
                            0 0 5px #ffffff,
                            0 0 10px #ffffff,
                            0 0 15px #ffffff,
                            0 0 20px #ffff00;
                        color: #ffffff;
                    }
                    50% { 
                        text-shadow: 
                            0 0 8px #ffff00,
                            0 0 16px #ffff00,
                            0 0 24px #ffff00,
                            0 0 32px #ffffff;
                        color: #ffff00;
                    }
                    100% { 
                        text-shadow: 
                            0 0 5px #ffffff,
                            0 0 10px #ffffff,
                            0 0 15px #ffffff,
                            0 0 20px #ffff00;
                        color: #ffffff;
                    }
                }
                
                @keyframes widget-glitch {
                    0% { 
                        transform: translate(0, 0) scale(1);
                        filter: hue-rotate(0deg) contrast(1) brightness(1);
                    }
                    10% { 
                        transform: translate(-5px, 2px) scale(1.02);
                        filter: hue-rotate(90deg) contrast(3) brightness(0.5);
                    }
                    20% { 
                        transform: translate(3px, -4px) scale(0.98);
                        filter: hue-rotate(180deg) contrast(0.5) brightness(2);
                    }
                    30% { 
                        transform: translate(-2px, 3px) scale(1.01);
                        filter: hue-rotate(270deg) contrast(2) brightness(0.8);
                    }
                    40% { 
                        transform: translate(4px, -1px) scale(0.99);
                        filter: hue-rotate(45deg) contrast(4) brightness(0.3);
                    }
                    50% { 
                        transform: translate(-3px, -2px) scale(1.03);
                        filter: hue-rotate(135deg) contrast(0.3) brightness(1.5);
                    }
                    60% { 
                        transform: translate(2px, 4px) scale(0.97);
                        filter: hue-rotate(225deg) contrast(2.5) brightness(0.6);
                    }
                    70% { 
                        transform: translate(-4px, 1px) scale(1.01);
                        filter: hue-rotate(315deg) contrast(1.5) brightness(1.2);
                    }
                    80% { 
                        transform: translate(1px, -3px) scale(0.99);
                        filter: hue-rotate(60deg) contrast(3.5) brightness(0.4);
                    }
                    90% { 
                        transform: translate(-1px, 2px) scale(1.02);
                        filter: hue-rotate(120deg) contrast(0.8) brightness(1.8);
                    }
                    100% { 
                        transform: translate(0, 0) scale(1);
                        filter: hue-rotate(0deg) contrast(1) brightness(1);
                    }
                }
                
                .reka-alert {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background: linear-gradient(45deg, #ff0000, #cc0000, #ff0000, #cc0000);
                    background-size: 400% 400%;
                    color: #ffffff;
                    padding: 16px 32px;
                    border: 3px solid #ff0000;
                    border-radius: 8px;
                    font-family: 'Courier New', monospace;
                    font-size: 18px;
                    font-weight: 900;
                    letter-spacing: 4px;
                    z-index: 9999;
                    animation: 
                        reka-alert-blink 0.6s infinite,
                        reka-alert-pulse 1.2s infinite,
                        reka-alert-text-glow 0.8s infinite;
                    box-shadow: 
                        0 0 30px rgba(255, 0, 0, 1),
                        0 0 60px rgba(255, 0, 0, 0.8),
                        inset 0 0 20px rgba(255, 100, 100, 0.3);
                    text-transform: uppercase;
                    position: fixed;
                    overflow: hidden;
                }

                .reka-alert::before {
                    content: '';
                    position: absolute;
                    top: -2px;
                    left: -2px;
                    right: -2px;
                    bottom: -2px;
                    background: linear-gradient(45deg, 
                        transparent, 
                        rgba(255, 255, 255, 0.3), 
                        transparent, 
                        rgba(255, 255, 255, 0.3)
                    );
                    background-size: 200% 200%;
                    animation: reka-alert-shine 2s infinite;
                    border-radius: 8px;
                    z-index: -1;
                }

                .reka-alert::after {
                    content: '⚠ DÉTECTION CONFIRMÉE ⚠';
                    position: absolute;
                    top: -25px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 10px;
                    font-weight: bold;
                    color: #ffff00;
                    text-shadow: 0 0 8px #ffff00;
                    letter-spacing: 1px;
                    white-space: nowrap;
                }

                @keyframes reka-alert-shine {
                    0% { 
                        background-position: -200% -200%; 
                    }
                    100% { 
                        background-position: 200% 200%; 
                    }
                }
                
                .reka-image {
                    mix-blend-mode: multiply;
                    pointer-events: none;
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                    opacity: 0;
                    animation: 
                        reka-fade-in-only 2s ease-in-out forwards,
                        reka-zoom-progressive 270s linear forwards,
                        reka-instability 0.8s infinite 2s,
                        reka-micro-movements-no-transform 1.2s infinite 2s;
                    transform-origin: center center;
                }
                
                .reka-image.fade-out {
                    animation: reka-fade-out 1s ease-in-out forwards;
                }
            `}</style>
            
            {/* Image fantomatique glitchée en arrière-plan */}
            {showGlitchImage && (
                <div className="glitch-image-container">
                    <img 
                        src={aliceGlitchImage}
                        alt="" 
                        className="glitch-image"
                    />
                </div>
            )}
            
            {/* Image de Reka en arrière-plan avec opacité faible */}
            {showRekaImage && (
                <div className="reka-image-container">
                    <div className="reka-image-wrapper">
                        <img 
                            src="./reka.f.jpg"
                            alt="" 
                            className={`reka-image ${rekaImageFadeOut ? 'fade-out' : ''}`}
                            style={rekaImageFadeOut ? {
                                transform: `scale(${rekaZoomLevel}) translate(${15 * (rekaZoomLevel - 1) / 5}%, ${8 * (rekaZoomLevel - 1) / 5}%)`,
                                animation: 'none'
                            } : {}}
                        />
                    </div>
                </div>
            )}
            
            {/* Deuxième image de Reka (reka.i.jpg) qui apparaît à la fin */}
            {showRekaSecondImage && (
                <div className="reka-second-image-container">
                    <div className="reka-second-image-wrapper">
                        <img 
                            src="./reka.i.jpg"
                            alt="" 
                            className={`reka-second-image ${rekaSecondImageFadeOut ? 'fade-out' : ''}`}
                        />
                    </div>
                </div>
            )}
            
            {/* Alerte ACTIVE clignotante - SÉPARÉE pour être centrée sur tout l'écran */}
            {showRekaAlert && showRekaSecondImage && (
                <div className="reka-alert">
                    ◆ ACTIVE ◆
                </div>
            )}
            
            <div className="border-b border-zinc-800 pb-1 mb-1 text-zinc-500 font-bold tracking-widest uppercase flex justify-between items-center flex-shrink-0 relative z-10">
                <span>{'>'} {t('terminalLogs.diagnosticEngine')}_v4.4</span>
                <button 
                    onClick={() => setShowInput(!showInput)}
                    className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors"
                >
                    {showInput ? `[${t('terminalLogs.esc')}]` : `[${t('terminalLogs.cmd')}]`}
                </button>
            </div>
            
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className={`flex-1 overflow-y-auto font-mono leading-tight space-y-0.5 scrollbar-hide relative z-10 transition-opacity duration-500 ${hideLogsForReka ? 'opacity-0' : 'opacity-100'}`}
            >
                {lines.map((line, i) => {
                    // Détecter les logs d'intrusion (ceux qui contiennent ⚠ ou ALERTE ou ERREUR_CRITIQUE)
                    const isIntrusionLog = line.includes('⚠') || line.includes('ALERTE') || line.includes('ERREUR_CRITIQUE') || line.includes('INTRUSION');
                    
                    return (
                        <div key={i} className={`${isIntrusionLog ? 'text-red-600/80 hover:bg-red-900/10' : 'text-cyan-600/80 hover:bg-cyan-900/10'} cursor-default flex gap-2`}>
                            <span className={isIntrusionLog ? 'text-red-800 shrink-0' : 'text-cyan-800 shrink-0'}>{line.split(']')[0]}]</span>
                            <span className={isIntrusionLog ? 'text-red-500' : 'text-cyan-500'}>{line.split(']').slice(1).join(']')}</span>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            {/* Zone de saisie de commande */}
            {showInput && (
                <div className="border-t border-zinc-800 pt-1 mt-1 flex-shrink-0 relative z-10">
                    <div className="flex items-center gap-2">
                        <span className="text-cyan-500 text-[10px]">{'>'}</span>
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="flex-1 bg-transparent text-cyan-500 text-[11px] outline-none border-none"
                            placeholder={t('terminalLogs.typeCommand')}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
